import { CommunityStyle, SharedImage, StyleInterface } from '../../types';
import useGetPostedImages from '../feed/useGetPostedImages';

type StyleMapItem = {
  count: number;
  totalAmountOfRemixes: number;
  style: StyleInterface;
  firstImageUrl: string;
};

/**
 * Hook to retrieve all unique styles used in community images.
 * This hook fetches posted images and extracts all unique styles from them.
 * @returns A function that returns an array of community styles with usage stats
 */
const useGetCommunityStyles = () => {
  const getPostedImages = useGetPostedImages();

  /**
   * Fetches hot community images using the getPostedImages hook
   * @returns Promise resolving to an array of SharedImage objects
   */
  const fetchImages = async () => {
    return await getPostedImages({
      sort: 'hot',
      channelList: [],
    });
  };

  /**
   * Main function that orchestrates the process of retrieving and processing community styles
   * @returns Promise resolving to an array of community styles sorted by popularity
   */
  const getCommunityStyles = async (): Promise<CommunityStyle[]> => {
    try {
      const images = await fetchImages();
      const communityStyles = processImagesToStyles(images);
      const sortedStyles = sortByPopularity(communityStyles);

      return sortedStyles;
    } catch (error) {
      console.error('Error fetching community styles:', error);
      return [];
    }
  };

  return getCommunityStyles;
};

export default useGetCommunityStyles;

/**
 * Processes shared images directly into community styles
 * @param images Array of shared images to extract styles from
 * @returns Array of CommunityStyle objects with usage statistics
 */
const processImagesToStyles = (images: SharedImage[]): CommunityStyle[] => {
  const styleMap = new Map<string, StyleMapItem>();

  // First, collect style information into a map
  images.forEach((sharedImage) => {
    const style = sharedImage.image.style;
    // Skip styles with names "No preset" or "No style"
    if (style && style.name !== 'No preset' && style.name !== 'No style') {
      const styleId = style.id;
      const imageUrl = sharedImage.image.imageUrl;
      const remixCount = sharedImage.amountOfRemixes || 0;

      // Process style names
      let processedStyle = style;

      if (style.name === 'Not style') {
        processedStyle = { ...style, name: 'Only Negatives' };
      } else if (style.name === 'No Style Biglust') {
        processedStyle = { ...style, name: 'Realistic XL with Negatives' };
      } else if (style.name === 'No style') {
        // Skip this style entirely
        return;
      }

      if (styleMap.has(styleId)) {
        const styleInfo = styleMap.get(styleId)!;
        styleMap.set(styleId, {
          ...styleInfo,
          count: styleInfo.count + 1,
          totalAmountOfRemixes: styleInfo.totalAmountOfRemixes + remixCount,
        });
      } else {
        styleMap.set(styleId, {
          count: 1,
          style: processedStyle,
          firstImageUrl: imageUrl,
          totalAmountOfRemixes: remixCount,
        });
      }
    }
  });

  // Convert the map directly to community styles
  return Array.from(styleMap.values()).map((info) => ({
    amountOfImages: info.count,
    style: info.style,
    imageUrlOfFirstImage: info.firstImageUrl,
    totalAmountOfRemixes: info.totalAmountOfRemixes,
  }));
};

/**
 * Sorts community styles by their popularity (number of images or total remixes)
 * @param styles Array of community styles to sort
 * @param sortBy Optional parameter to specify the sort criteria - 'images' (default) or 'remixes'
 * @returns New array of community styles sorted by descending popularity
 */
const sortByPopularity = (
  styles: CommunityStyle[],
  sortBy: 'images' | 'remixes' = 'remixes',
) => {
  return [...styles].sort((a, b) => {
    if (sortBy === 'remixes') {
      return b.totalAmountOfRemixes - a.totalAmountOfRemixes;
    }
    return b.amountOfImages - a.amountOfImages;
  });
};
